import { ToastContainer, toast as rtToast, cssTransition } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useCallback, type ReactNode } from 'react';

const SmoothTransition = cssTransition({
  enter: 'rt-slide-in',
  exit: 'rt-fade-out',
  collapseDuration: 250,
});

const TOAST_CLASS =
  '!bg-zinc-900 dark:!bg-zinc-50 !text-white dark:!text-zinc-900 !rounded-md !shadow-lg !text-sm !font-medium !min-h-0 !py-3 !px-4 !cursor-default';
const ERROR_CLASS =
  '!bg-red-600 !text-white !rounded-md !shadow-lg !text-sm !font-medium !min-h-0 !py-3 !px-4 !cursor-default';

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <style>{`
        @keyframes rt-slide-in-keyframes {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes rt-fade-out-keyframes {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        .rt-slide-in { animation: rt-slide-in-keyframes 0.3s cubic-bezier(0.21, 1.02, 0.73, 1) forwards; }
        .rt-fade-out { animation: rt-fade-out-keyframes 0.25s ease-in forwards; }
        :root { --toastify-font-family: 'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, system-ui, sans-serif; }
        @media (max-width: 480px) {
          .Toastify__toast-container { padding: 0 16px 12px; }
        }
      `}</style>
      <ToastContainer
        position="bottom-center"
        autoClose={2500}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover
        transition={SmoothTransition}
      />
    </>
  );
}

export interface ToastOptions {
  /**
   * 같은 id 의 토스트가 이미 떠 있으면 새로 띄우지 않는다.
   * 여러 쿼리가 동시에 같은 이유로 실패할 때(API 다운 등) 같은 문구가 겹쳐 뜨는 걸 막는다.
   */
  toastId?: string;
}

/**
 * React 훅이 아닌 토스트. 컴포넌트 밖(전역 에러 핸들러 등)에서 호출할 수 있다.
 * 컴포넌트 안에서는 `useToast()` 를 쓰는 편이 낫다 (동일 구현).
 */
export function toast(
  message: string,
  variant: 'success' | 'error' = 'success',
  options?: ToastOptions
) {
  rtToast(message, {
    className: variant === 'error' ? ERROR_CLASS : TOAST_CLASS,
    closeButton: false,
    ...(options?.toastId ? { toastId: options.toastId } : {}),
  });
}

export function useToast() {
  const show = useCallback(
    (message: string, variant: 'success' | 'error' = 'success', options?: ToastOptions) =>
      toast(message, variant, options),
    []
  );
  return { toast: show };
}
